import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Reveal, useSectionPhase } from './Reveal';
import { FeatureCard, AudienceCard, ProcessStep } from './cards';
import { DepartureBoard } from './DepartureBoard';
import {
  AUDIENCES,
  PROCESS_STEPS,
  SYNTHESIS_FEATURES,
  TRUSTED_BRANDS,
  delayClass,
} from './data';

export function HeroSection({ interval = 4200, pause = false, stagger = 28 }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 20 - 10,
        y: (e.clientY / window.innerHeight) * 20 - 10,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section data-cq-region="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center opacity-40">
        <div
          className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-brand/25 rounded-full mix-blend-screen filter blur-[100px] animate-organic"
          style={{ transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)` }}
        ></div>
        <div
          className="absolute -bottom-32 right-1/4 w-[500px] h-[500px] bg-brand-accent/20 rounded-full mix-blend-screen filter blur-[120px] animate-organic"
          style={{ animationDelay: '4s', transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)` }}
        ></div>
      </div>

      <div className="absolute inset-0 grain-noise opacity-[0.08] mix-blend-overlay"></div>

      <div className="relative z-10 mx-auto mt-12 w-full max-w-5xl px-6 text-left">
        <Reveal delay="delay-150">
          <h1 className="mb-8 font-semibold leading-[1.05] tracking-tight text-white">
            <span className="mb-4 block text-5xl text-glow-premium md:text-6xl lg:text-7xl">
              From clinical data
            </span>
            <DepartureBoard interval={interval} pause={pause} stagger={stagger} />
          </h1>
        </Reveal>

        <Reveal delay="delay-300">
          <p className="mb-10 max-w-3xl text-xl font-light leading-relaxed text-slate-300 md:text-2xl">
            We transform raw outcomes into submission-ready manuscripts. <br className="hidden md:block"/>
            <span className="font-medium text-white">Authored by PhDs. Automated by proprietary tech.</span>
          </p>
        </Reveal>

        <Reveal delay="delay-450">
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="#cq-consult"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-widest text-black shadow-[0_0_30px_rgba(255,255,255,0.15)] transition-colors duration-300 hover:bg-slate-200 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)]"
            >
              Initiate Project
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#cq-process"
              className="group inline-flex items-center gap-2 px-2 py-4 text-sm font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-white"
            >
              See the process
              <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>

      <a
        href="#cq-process"
        className="absolute bottom-10 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-white/50 transition-colors hover:text-white/80"
      >
        <span className="text-xs font-semibold uppercase tracking-widest">See the process</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </a>
    </section>
  );
}

export function TrustedBrandsSection() {
  const brands = [...TRUSTED_BRANDS, ...TRUSTED_BRANDS];

  return (
    <section data-cq-region="hero" className="py-10 border-b border-white/5 bg-white/[0.01] relative z-20 overflow-hidden flex flex-col items-center">
      <div className="max-w-7xl mx-auto px-6 mb-8 text-center w-full">
        <p className="text-xs font-bold tracking-widest text-slate-500 uppercase">Trusted by clinical teams at leading institutions</p>
      </div>

      <div className="relative flex overflow-hidden mask-edges w-full max-w-[100vw] group">
        <div className="flex whitespace-nowrap animate-marquee group-hover:[animation-play-state:paused] items-center w-max">
          {brands.map((brand, i) => (
            <div
              key={`${brand}-${i}`}
              aria-hidden={i >= TRUSTED_BRANDS.length || undefined}
              className="mx-10 md:mx-16 text-xl md:text-2xl font-black tracking-tighter text-slate-400 hover:text-brand-accent transition-colors duration-300 cursor-default"
            >
              {brand}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SynthesisSection() {
  const [primary, ...secondary] = SYNTHESIS_FEATURES;

  return (
    <section data-cq-region="body" className="py-32 relative z-10 bg-[#060606]">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="mb-20 max-w-3xl">
            <h2 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight text-white text-glow-premium">
              The perfect synthesis of <br/>
              human intellect & software.
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed font-light">
              We eliminated the administrative friction of medical writing. Our proprietary engine enforces rigorous journal formatting, freeing our clinical experts to dedicate 100% of their bandwidth to your science.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <Reveal delay="delay-150">
              <FeatureCard {...primary} />
            </Reveal>
          </div>

          <div className="lg:col-span-2 flex flex-col gap-6">
            {secondary.map((feature, idx) => (
              <Reveal key={feature.title} delay={delayClass((idx + 2) * 150)}>
                <FeatureCard {...feature} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhoWeServeSection() {
  const [ref, phase] = useSectionPhase();

  return (
    <section
      ref={ref}
      data-cq-region="body"
      className="relative overflow-hidden border-y border-white/5 bg-[#030303] py-32"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-brand-dark/20 blur-[120px]"></div>

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <Reveal>
          <div className="mb-16 max-w-3xl">
            <p className="mb-4 text-sm font-bold uppercase tracking-widest text-slate-400">Tailored Expertise</p>
            <h2 className="text-4xl font-semibold leading-tight text-white md:text-5xl">
              Built for every stakeholder in clinical evidence.
            </h2>
          </div>
        </Reveal>

        <div className="cq-serve-grid grid gap-8 md:grid-cols-3">
          {AUDIENCES.map((target, idx) => (
            <AudienceCard key={target.title} {...target} phase={phase} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function MidPageAnchor() {
  return (
    <section data-cq-region="body" className="py-28 bg-[#0A0A0A] border-y border-white/5 relative overflow-hidden flex items-center justify-center text-center px-6">
      <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none">
        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-brand-accent to-transparent"></div>
      </div>
      <Reveal>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-light text-slate-300 mb-8 leading-snug">
            Your clinical outcomes deserve to be published. <br className="hidden md:block" />
            <span className="text-white font-semibold">Not stalled in formatting.</span>
          </h2>
          <a
            href="#cq-consult"
            className="cq-cta-mid group mx-auto inline-flex items-center justify-center gap-3 rounded-full bg-white px-10 py-5 text-sm font-bold uppercase tracking-widest text-black shadow-[0_0_30px_rgba(255,255,255,0.15)] transition-colors duration-300 hover:bg-slate-200"
          >
            Initiate Project Review
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </Reveal>
    </section>
  );
}

function useProcessProgress(trackRef) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setProgress(1);
      return undefined;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = track.getBoundingClientRect();
      const readLine = window.innerHeight * 0.52;
      const start = rect.top;
      const span = Math.max(rect.height, 1);
      const next = Math.min(1, Math.max(0, (readLine - start) / span));
      setProgress((current) => (Math.abs(current - next) < 0.002 ? current : next));
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [trackRef]);

  return progress;
}

export function ProcessSection() {
  const trackRef = useRef(null);
  const progress = useProcessProgress(trackRef);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const dots = [...track.querySelectorAll('[data-cq-dot]')];
    if (!dots.length) return undefined;

    const rect = track.getBoundingClientRect();
    const fillY = rect.top + rect.height * progress;
    let next = -1;
    dots.forEach((dot, i) => {
      const mid = dot.getBoundingClientRect().top + dot.getBoundingClientRect().height / 2;
      if (mid <= fillY + 6) next = i;
    });
    setActiveIndex((current) => (current === next ? current : next));
  }, [progress]);

  return (
    <section id="cq-process" data-cq-region="body" className="scroll-mt-24 py-32 relative overflow-hidden bg-[#060606]">
      <div className="max-w-5xl mx-auto px-6 relative">
        <Reveal>
          <h2 className="text-4xl md:text-6xl font-semibold text-center mb-24 text-white text-glow-premium">The proven path to publication.</h2>
        </Reveal>

        <div ref={trackRef} className="relative">
          <div className="cq-process-track pointer-events-none absolute left-6 top-8 bottom-8 w-[2px] md:left-[120px]">
            <div className="absolute inset-0 bg-white/5" />
            <div
              className="cq-process-fill absolute left-0 top-0 w-full origin-top bg-brand-accent shadow-[0_0_12px_rgba(66,165,245,0.65)]"
              style={{ height: `${progress * 100}%` }}
            />
            <div
              className="cq-process-head absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-accent shadow-[0_0_16px_5px_rgba(66,165,245,0.45)]"
              style={{ top: `${progress * 100}%`, opacity: progress > 0.01 ? 1 : 0 }}
            />
          </div>

          <div className="space-y-8 relative z-10">
            {PROCESS_STEPS.map((step, idx) => (
              <Reveal key={step.num} delay={delayClass(idx * 100)} direction="up">
                <ProcessStep {...step} active={idx <= activeIndex} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function FooterCta() {
  return (
    <footer id="cq-consult" data-cq-region="footer" className="scroll-mt-16 relative py-32 border-t border-white/10 overflow-hidden flex flex-col items-center justify-center text-center px-6 bg-[#030303] z-10">
      <Reveal>
        <div className="relative z-10 flex flex-col items-center">
          <h2 className="text-4xl md:text-6xl font-semibold mb-10 text-white text-glow-premium tracking-tight">
            Ready to publish your outcomes?
          </h2>

          <button className="group relative inline-flex items-center justify-center px-10 py-5 font-bold text-sm tracking-widest uppercase rounded-full bg-white text-black hover:bg-slate-200 transition-colors duration-300 shadow-[0_0_30px_rgba(255,255,255,0.15)] hover:shadow-[0_0_40px_rgba(255,255,255,0.3)]">
            <span className="flex items-center gap-3">
              Schedule a Consultation
              <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>
      </Reveal>

      <div className="absolute bottom-8 w-full flex justify-between items-center px-8 text-xs font-bold text-slate-500 uppercase tracking-widest">
        <span>© 2026 Axiom Medical Publications</span>
        <div className="flex gap-8">
          <a href="#" className="hover:text-slate-300 transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-slate-300 transition-colors">Contact</a>
        </div>
      </div>
    </footer>
  );
}

export function HomeView() {
  return (
    <>
      <HeroSection />
      <TrustedBrandsSection />
      <SynthesisSection />
      <WhoWeServeSection />
      <MidPageAnchor />
      <ProcessSection />
    </>
  );
}
